import request, {
  shopRequest,
  uploadGoodAttachmentRequest,
  uploadS3Request,
} from 'api/index'
import {
  PaymentMethod,
  SaleStatus,
  TypeIndicator,
  UsagePeriodUnitType,
  TYPE_INDICATOR_PRODUCT,
  TypeIndicatorProduct,
  KeyPrices,
  OffersType,
  TypeDeliveryStatus,
  TypeGoodsType,
  KeyPayment,
  TypeOrderStatus,
  TypePaymentStatus,
  TypeCancellationStatus,
  TypePaymentType,
  TypePaymentChannel,
  ALL_SALE_TARGET,
  TypeOrgType,
  Prepayment,
  TypeReasonCode,
  PaymentTypeOrg,
  SearchValueTypeOrg,
} from 'pages/ProductsPages/constant'

import {TypePackageType, TypePackageTypeValue} from 'constants/AnalysisConstant'
import {LicenseStatus} from 'pages/ProductOperationOrganization/constant'

/* =====================
 * Common helpers (internal)
 * ===================== */
type BaseDisplay = {
  langCode: string
  name: string
  description: string
}

type PageMeta = {
  number: number
  size: number
  totalPages: number
  totalElements: number
}

// Reuse shorthand
type Money = PricesInterface
type LangDisplays = DisplayInfosType[]

/* =====================
 * Keep original export names — but refactor to reuse
 * ===================== */

export interface GoodDisplay extends BaseDisplay {}

export interface ProductManagementParam {
  page: number
  size: number
  targets: string
  paymentMethod: string
  goodsName: string | null
}

export interface ListPackageResponse {
  itemId: number | string
  cls: string
  target: TypeIndicator
  type: TypePackageTypeValue
  prerequisites: TypePackageTypeValue[]
  displayInfos: LangDisplays
  unitPrices: PricesInterface[]
  select?: boolean
  offerAmount?: number
  order: number
}

export interface ListProductType {
  goods: GoodsResponse
  sale: SaleType
  offer: OfferType
}

export type ListProductPagingResponse = {
  content: Array<ListProductType>
} & PageMeta

export interface UnitPricesType {
  currency: string
  price: number
  itemId: number | string
  cls: string
  target: string
  type: string
  displayInfos: LangDisplays
  unitPrices: PricesInterface[]
}

export interface GoodsResponse {
  goodsId: string
  goodsName: GoodDisplay[]
  saleStatus: SaleStatus
  isEditable: boolean
  isArchived: boolean
}

/** Common sale core reused below */
type SaleCore = {
  saleTarget: SaleTarget
  paymentMethod: PaymentMethod
}

export interface SaleType extends SaleCore {
  salePrice: PricesInterface[]
  price: PricesInterface[]
}

export interface OfferType {
  usagePeriodUnit: UsagePeriodUnitType
  usagePeriod: number
  offerType: string
  offerItems: OfferItemAdmin[]
}

export enum SearchKeys {
  all = 'all',
  hospital = 'hospital',
  checkup = 'checkup',
  personal = 'personal',
  charge = 'charge',
  subscription = 'subscription',
  settlement = 'settlement',
  productName = 'productName',
}

export interface ProductManagementSearchRequest {
  targets?: string
  excludeTargets?: string
  paymentMethod?: string
  goodsName?: string
  offerType?: string
  saleStatus?: string
  archived?: boolean
}

export interface ProductManagementSearchPagingRequest
  extends ProductManagementSearchRequest {
  page: number
  size: number
}

export interface PackageListRequest {
  target: string
}

export interface ProductPurchaseHistoryPayload {
  paymentDateFrom: string
  paymentDateTo: string
  goodsName?: string
  paymentNo?: string
}

export interface ProductPurchaseHistoryOrganizationPayload {
  page: number
  size: number
  paymentDateFrom: string
  paymentDateTo: string
  goodsName?: string
  paymentNo?: string
  orderStatus?: string
  orgName?: string
  agencyId?: number | null
}
export type PriceKey = 'unitPriceWon' | 'unitPriceDollar' | 'paidPoints'

export interface Product {
  id: number
  key: string
  select: boolean
  nameProduct: string
  unitPriceWon: number
  unitPriceDollar: number
  paidPoints: number
  deductionPoints: number
  remark: string
  unitPbm?: string
}

export interface GoodsClsType {
  type: 'Single'
  maxOwnLimit: number
  usagePriority: number
  id: string
}

export interface ApiUserDetailGoodsType {
  goodsId: string
  goodsName: GoodDisplay[]
}

export interface ApiAdminDetailGoodsType {
  goodsId: string
  isEditable: false
  saleStatus: SaleStatus
  displayInfos: GoodsDisplayInfoType[]
  attachment: {
    name: string
    url: string
  } | null
}

/** Unify display info shapes */
export type DisplayInfosType = BaseDisplay & {imageUrl: string}

export interface GoodsDisplayInfoType extends DisplayInfosType {}

export interface GroupProduct {
  groupName: string
  products: Product[]
}

export interface GroupTableRowProps {
  group: GroupProduct
  product: Product
  index: number
  parentIndex: number
  onSelectChange: (parentIndex: number, index: number, value: boolean) => void
  updatePriceField: (
    parentIndex: number,
    index: number,
    key: PriceKey,
    value: number,
  ) => void
  onChangePbmUnit: (value: string) => void
}

export type SaleTarget = {
  targetOp: string
  targets: Array<{
    targetType: string
    targetId: string
  }>
}

export type OfferItem = {
  offerAmount: number
  offerItemId: string
  offerSpecId: string
  offerUnit: string
  pkgItemId: string
  unitPrices: PricesInterface[]
}

export type OfferItemAdmin = {
  offerAmount: number
  offerUnit: string
  pkgItemId: string
  unitPrices: PricesInterface[]
  id: string
}

/** ====== API Sale: refactor to a shared base ====== */
type APISaleBase = {
  name: string
  description: string
  prices: PricesInterface[]
  discountRate: number
  saleStartAt: number
  saleEndAt: number
  totalQuantity: number
  maxQuantityPerOrder: number
  paymentMethod: PaymentMethod
  paymentTypes: string[]
  saleTarget: SaleTarget
  canCharge: boolean
  chargeMode: ChargeModeType
  chargeQuantity: number | null
}

export type APIDetailSaleType = APISaleBase & {
  salePrice: PricesInterface[]
  price: PricesInterface[]
}

export type ChargeModeType = 'Each' | 'All'

export type APIAdminDetailSaleType = APISaleBase & {
  salePrices: PricesInterface[]
  chargeMode: ChargeModeType
  chargeQuantity: number | null
}

export type APIListSaleType = {
  canCharge: boolean
  paymentMethod: PaymentMethod
  salePrice: PricesInterface[]
  saleTarget: SaleTarget
}

type APIUserDetailOfferType = {
  usagePeriodUnit: string
  usagePeriod: number
  offerType: Prepayment
  offerItems: OfferItem[]
}

type APIAdminDetailOfferType = {
  id: string
  usagePeriodUnit: string
  usagePeriod: number
  offerType: Prepayment
  offerItems: OfferItemAdmin[]
}

export interface ProductUserDetailResponse {
  goods: ApiUserDetailGoodsType
  sale: APIDetailSaleType
  offer: APIUserDetailOfferType
}

export interface ProductAdminDetailResponse {
  goods: ApiAdminDetailGoodsType
  sale: APIAdminDetailSaleType
  offer: APIAdminDetailOfferType
}

/** ====== Param responses share the same structure for goods/displayInfos ====== */
type ProductParamBase = {
  goods: {displayInfos: LangDisplays}
  id?: string
}

export interface ProductParamResponse extends ProductParamBase {
  offer: SpecPart
  sale: SalePart
}

export interface AdminProductParamResponse extends ProductParamBase {
  offer: SpecPart
  sale: AdminSalePart
}

export interface SpecPart {
  usagePeriodUnit: UsagePeriodUnitType
  usagePeriod: number
  offerType: OffersType
  offerItems: OfferItemsType[]
}

/** SalePart / AdminSalePart share a core */
type SalePartCore = {
  prices: PricesInterface[]
  discountRate: number
  paymentMethod: string
  saleTarget: SaleTarget | null
  chargeMode: ChargeModeType
  chargeQuantity: number | null
}

export interface SalePart extends SalePartCore {
  salePrice: PricesInterface[]
}

export interface AdminSalePart extends SalePartCore {
  salePrices: PricesInterface[]
}

export type OfferItemsType = {
  offerUnit: string
  offerAmount: number
  unitPrices: PricesInterface[]
  pkgItemId: string
  quantity?: number
}

export interface PricesInterface {
  currency: KeyPrices
  price: number
}

export interface SaleTargetInterface {
  targetType: string
  targetId: string
}

/** ====== Inquiry / Purchase history: reuse Order/Payment/Cancellation ====== */
export type ProductInquiryType = {
  content: Array<ProductPurchaseHistoryResponse>
} & PageMeta

export type EnumActiveLicenses = 'ACTIVE' | 'INACTIVE' | 'PAUSE'
export type LicenseAnalyzeStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'

export type ProductPurchaseHistoryResponse = {
  cancellations: Cancellation[] // reuse
  order: Order
  payment: Payment
}

/** ====== Licenses ====== */
export type ListLicensesLogs = {
  offerLogs: OfferLogsType[]
  usageLogs: UsageLogsType[]
}

export type UsageLogsType = {
  analyzeStatus: LicenseAnalyzeStatus
  licenseItemId: string
  usageId: string
  usageQuantity: number
  usageDateTime: number
  usageBy: string
}

export type OfferLogsType = {
  licenseItemId: string
  method: string
  offerQuantity: number
  offerBy: string
  reason: string
  reasonCode: TypeReasonCode
  offerDateTime: number
}

export type ListLicenses = {
  id: string
  orgId: string
  goodsId: string
  activationDateTime: number
  deactivationDateTime: number
  status: LicenseStatus
  priority: number
  items: {
    id: string
    licenseId: string
    packageItemId: string
    offerQuantity: number
    usageQuantity: number
    quantityUnit: string
    priority: number
    activationDateTime: number
    deactivationDateTime: number
  }[]
  latestStatusLog: {
    status: LicenseStatus
    changeDateTime: number
    changedBy: string
    changedByName: string
    reasonCode: string
    reason: string
  }
}

export type ActiveLicenses = {
  id: string
  goodsId: string
  activationDateTime: number
  deactivationDateTime: number
  status: EnumActiveLicenses
  priority: number
  items: ILicenseItem[]
}

export interface UpdateDisplayInfoProductResponse {
  offerSpecId: string
  saleSpecId: string
  saleTargetSpecId: string
  cls: {
    type: 'Single' | 'NonDuplicate' | 'Multiple'
    maxOwnLimit: number
    usagePriority: number
    id: string
  }
  displayInfos: GoodsDisplayInfoType[]
  saleStatus: 'Ready' | 'OnSale' | 'OffSale'
  isEditable: boolean
  refillableGoodsId: string
  id: string
}

export interface AvailableLicenses extends Omit<ActiveLicenses, 'goodsId'> {
  licenseId: string
  packageItemId: string
  offerQuantity: number
  usageQuantity: number
  quantityUnit: string
}

/** ====== Product available ====== */
export type ProductAvailableType = {
  goods: {
    goodsId: string
    goodsName: GoodDisplay[]
    displayInfos?: GoodsDisplayInfoType[]
    orderRank: string
  }
  sale: {
    salePrice: PricesInterface[]
    price: PricesInterface[]
    paymentMethod: PaymentMethod
    canCharge: boolean
    saleTarget: SaleTarget
    chargeMode: ChargeModeType
    chargeQuantity: number | null
  }
  offer: {
    usagePeriodUnit: UsagePeriodUnitType
    usagePeriod: number
    offerType: string
    offerItems: OfferItemsType[]
  }
}

/** ====== Requests share Customer payload ====== */
export interface ShopOrderRequest {
  goodsId: string
  currency: KeyPrices
  customer: Customer
  agreeToOrder: boolean
}

export interface ShopChargeRequest {
  licenseId: string
  currency: string
  chargeItems: {
    offerItemId: string
    quantity: number
  }[]
  customer: Customer
  agreeToOrder: boolean
}

export interface ShopPaymentRequest {
  orderId: string
  paymentType: KeyPayment
  customer: Customer
}

/* =====================
 * Core Order/Payment/Cancellation reused everywhere
 * ===================== */
export interface Order {
  orderId: string
  orgId: string
  orderAt: number
  orderStatus: TypeOrderStatus
  price: Money
  orderItems: OrderItem[]
}

export interface OrderItem {
  orderItemId: string
  goodsType: TypeGoodsType
  goodsId: string
  chargeId: string
  goodsPrice: Money
  purchaseCount: number
  totalPrice: Money
  deliveryStatus: TypeDeliveryStatus
}

export interface Payment {
  paymentId: string
  orderId: string
  paymentNo: string
  paymentChannel: TypePaymentChannel
  paymentType: TypePaymentType
  paymentTypeName: string
  price: Money
  canceledPrice: Money
  status: TypePaymentStatus
  paymentAt: number // timestamp
  receiptUrl: string
  paymentUserId: string
  customer: Customer
}

export interface PaymentStatusResponse {
  canCancel: boolean
  reason: string
  paymentInfo: Payment
}

export interface Customer {
  name: string
  email: string
  phoneNumber: string
}

export interface PaymentCustomer {
  fullName: string
  email: string
  phoneNumber: string
}

export interface Cancellation {
  paymentId: string
  paymentChannel: TypePaymentChannel
  paymentType: TypePaymentType
  status: TypeCancellationStatus
  price: Money
  canceledAt: number
  receiptUrl: string
  reason: string
}

export interface PaymentInformationResponse {
  order: Order
  payment: Payment
  cancellations: Cancellation[]
}
export interface ILicenseItem {
  id: string
  licenseId: string
  packageItemId: string
  offerQuantity: number
  usageQuantity: number
  quantityUnit: string
  priority: number
  activationDateTime: number
  deactivationDateTime: number
}

export interface ISalesSlip {
  paymentId: string
  salesSlipNo: string
  issueDate: string
  buyer: {
    orgName: string
    address: string
    email: string
  }
  seller: {
    name: string
  }
  product: {
    name: string
    quantity: number
    unitPrice: number
    price: number
    currency: KeyPrices
  }
  remark: string
}

export interface IUpdateSalesSlipRequest {
  paymentId: string
  issueDate: string
  buyer: {
    orgName: string
    address: string
    email: string
  }
  seller: {
    name: string
  }
  product: {
    name: string
    quantity: number
    unitPrice: number
    price: number
  }
  remark: string
}

/* =====================
 * ShopOrderResponse — reuse Payment/Customer via Omit/Pick to avoid duplication
 * ===================== */
export interface ShopOrderResponse {
  orderId: string
  orgId: string
  orderAt: string
  price: Money
  orderItems: Array<{
    orderItemId: string
    goodsType: TypeGoodsType
    goodsId: string
    chargeId: string
    goodsPrice: Money
    purchaseCount: number
    totalPrice: Money
    deliveryStatus: TypeDeliveryStatus
  }>
  payment: Omit<
    Payment,
    'paymentChannel' | 'paymentType' | 'status' | 'paymentAt' | 'receiptUrl'
  > & {
    paymentChannel: string
    paymentType: string
    status: string
    paymentAt: null | Date
    receiptUrl: null | Date
  }
  pgPaymentRequest: {
    storeId: string
    channelKey: string
    paymentId: string
    orderName: string
    totalAmount: number
    currency: KeyPrices
    payMethod: string
    customer: PaymentCustomer
    locale: string
  }
}

export type ShopPaymentResponse = Omit<
  Payment,
  'paymentChannel' | 'paymentType' | 'status' | 'paymentAt' | 'receiptUrl'
> & {
  paymentChannel: string
  paymentType: string
  status: string
  paymentAt: Date
  receiptUrl: string
}

export interface ListOrgCanSellProductResponse {
  code: number
  debugMessage: string
  message: string
  page: {
    content: IContentOrg[]
    number: number
    size: number
    totalElements: number
    totalPages: number
  }
  success: boolean
}
export type IContentOrg = {
  chiefUser: {
    email: string
    firstName: string
    id: number
    lastName: string
    uid: string
  }
  features: string[]
  org: {
    address: string
    businessNo: string
    countryCode: string
    createdAt: number
    id: number
    industryDetailId: number
    industryId: number
    institutionNo: string
    name: string
    oid: string
    twoFactorType: string
    type: string
  }
}
export interface FetchListOrgCanSellProductRequest {
  goodsId: string
  payload: {
    page?: number
    size?: number
    orgName?: string
    email?: string
  }
}

export interface OrgByLicenseDetail {
  licenseId: string
  licenseActivationAt: string
  licenseDeactivationAt: string
  licenseStatus: LicenseStatus
  goodsId: string
  goodsName: string
  paymentType: PaymentTypeOrg
  remark: string
}

export interface SearchOrgByLicenseRequest {
  page?: number
  size?: number
  lang?: string
  orgType?: TypeOrgType
  domestic?: boolean
  paymentType?: PaymentTypeOrg
  searchValueType?: SearchValueTypeOrg
  searchValue?: string
  orgAppointed?: boolean
  freePayment?: boolean
}

export type OrgByLicense = {
  orgId: string
  orgName: string
  email: string
  countryCode: string
  licenses: OrgByLicenseDetail[]
}

export type OrgByLicenseInquiryType = {
  content: Array<OrgByLicense>
} & PageMeta

/** ============= Invoice Target ============== */

export type InvoiceTarget = {
  orgId: string
  billingMonth: string
  billingPrice: number
  reportFileUrl: string
  issued: boolean
}
export interface GetInvoiceTargetListRequest {
  page: number
  size: number
  from: string
  to: string
  orgName?: string
  region?: string
}
export interface GetInvoiceTargetListResponse extends PageMeta {
  content: InvoiceTarget[]
}

/** ============= Invoice Information =============== */
export type InvoiceInformation = {
  id?: string
  supplier?: {
    companyName: string
    businessNumber: string
    ceoFirstName: string
    ceoLastName: string
    address: string
    contactNumber: string
    contactName: string
  }
  consumer?: {
    orgId: number
    name: string
    email: string
  }
  billing?: {
    issueDate?: string
    billingMonth?: string
    billingPrice?: number
    remarks?: string
    fileUrl?: string
    issued?: boolean
  }
}
export interface GetInvoiceByOrgAndMonthRequest {
  orgId: string
  billingMonth: string
}

export interface CreateInvoicePayload {
  orgId: number
  billingMonth: string
  billingPrice: number
  issueDate: string
  remarks: string
  registerReportFile: {
    key: string
    etag: string
  }
}

export interface UpdateInvoicePayload {
  invoiceId: string
  billingPrice: number
  issueDate: string
  remarks: string
  registerReportFile: {
    key: string
    etag: string
  }
}

/** ================ Invoice File ================== */
export interface InvoiceFileUploadUrl {
  fileKey: string
  uploadUrl: string
}
export interface GetInvoiceFileUploadUrlRequest {
  orgId: string
  billingMonth: string
}

/** ============= License by month ================== */
export type LicenseByMonth = {
  orgId: string
  billingMonth: string
  licenses: {
    licenseId: string
    goodsId: string
  }[]
}
export interface GetLicenseByOrgAndMonthRequest {
  orgId: string
  billingMonth: string
}

/** ============= Register Manual License ============== */
export type RegisterManualLicensePayload = {
  quantities: {
    itemId: string
    amount: number
  }[]
  reasonCode: string
  reason: string
  changedByName: string
}

/** ============= Register Manual License ============== */

export type FileUploadUrlResponse = {
  fileKey: string
  uploadUrl: string
  headers: {
    'content-disposition': string
    'content-type': string
    host: string
  }
}

export const fetchPackageListApi = (query: PackageListRequest) => {
  let params: TypeIndicatorProduct[] = []
  if (query.target === TYPE_INDICATOR_PRODUCT.HOSPITAL) {
    params = [TYPE_INDICATOR_PRODUCT.HOSPITAL, TYPE_INDICATOR_PRODUCT.ALL]
  }
  if (query.target === TYPE_INDICATOR_PRODUCT.HEALTH) {
    params = [TYPE_INDICATOR_PRODUCT.HEALTH, TYPE_INDICATOR_PRODUCT.ALL]
  }
  if (query.target === TYPE_INDICATOR_PRODUCT.ALL) {
    params = [
      TYPE_INDICATOR_PRODUCT.ALL,
      TYPE_INDICATOR_PRODUCT.HOSPITAL,
      TYPE_INDICATOR_PRODUCT.HEALTH,
    ]
  }

  const queryString = params.map((t) => `target[]=${t}`).join('&')
  return shopRequest<ShopModelResponse<ListPackageResponse[]>>({
    method: 'get',
    path: `${'/shop/product/v1/user/package/item?'}${encodeURI(queryString)}`,
  })
}

export const fetProductPurchaseHistory = (
  payload: ProductPurchaseHistoryPayload,
) =>
  shopRequest<ShopModelResponse<ProductPurchaseHistoryResponse[]>>({
    method: 'get',
    path: `/shop/orders/v1/user/bff/payment`,
    params: payload,
  })

export const fetchListLicensesLogs = (licenseId: string) =>
  shopRequest<ShopModelResponse<ListLicensesLogs>>({
    method: 'get',
    path: `/shop/licenses/v1/user/license/${licenseId}/logs`,
  })

export const fetchListLicenses = () =>
  shopRequest<ShopModelResponse<ListLicenses[]>>({
    method: 'get',
    path: `/shop/licenses/v1/user/license`,
  })

export const fetchActiveLicenses = () =>
  shopRequest<ShopModelResponse<ActiveLicenses[]>>({
    method: 'get',
    path: `/shop/licenses/v1/user/license/valid`,
  })

export const fetchDetailProduct = (id: string) =>
  shopRequest<ShopModelResponse<ProductAvailableType>>({
    method: 'get',
    path: `/shop/product/v1/user/bff/goods/${id}`,
  })

export const fetchProductInquiryApi = () =>
  shopRequest<ShopModelResponse<ActiveLicenses[]>>({
    method: 'get',
    path: '/shop/licenses/v1/user/license/valid',
  })

export const fetchProductAvailableApi = () =>
  shopRequest<ShopModelResponse<ProductAvailableType[]>>({
    method: 'get',
    path: '/shop/product/v1/user/bff/goods',
  })

export const fetchProductDetailApi = (query: {goodsId: string}) =>
  shopRequest<ShopModelResponse<ProductAdminDetailResponse>>({
    method: 'get',
    path: `/shop/product/v1/admin/bff/goods/${query.goodsId}`,
  })

export const fetchProductDetailUserApi = (query: {goodsId: string}) =>
  shopRequest<ShopModelResponse<ProductUserDetailResponse>>({
    method: 'get',
    path: `/shop/product/v1/user/bff/goods/${query.goodsId}`,
  })

export const fetchListProductDetailUserApi = (ids: string[]) =>
  shopRequest<ShopModelResponse<ProductUserDetailResponse[]>>({
    method: 'get',
    path: `/shop/product/v1/user/bff/goods/by-ids`,
    params: {ids: ids.join(',')},
  })

export const fetchProductListApi = (query: ProductManagementSearchRequest) =>
  shopRequest<ShopModelResponse<ListProductType[]>>({
    method: 'get',
    path: '/shop/product/v1/admin/bff/goods/search',
    params: query,
  })

export const fetchProductListPagingApi = (
  query: ProductManagementSearchPagingRequest,
) =>
  shopRequest<ShopModelResponse<ListProductPagingResponse>>({
    method: 'get',
    path: '/shop/product/v1/admin/bff/goods',
    params: query,
  })

export const fetchUpdateStatusApi = ({
  status,
  goodsId,
}: {
  status: SaleStatus
  goodsId: string
}) =>
  shopRequest<ShopModelResponse<GoodsResponse>>({
    method: 'put',
    path: `/shop/product/v1/admin/goods/${goodsId}/status`,
    data: {status},
  })

export const fetchCreateProductApi = (data: AdminProductParamResponse) => {
  if (data.sale.saleTarget?.targets.length === 0) {
    data.sale.saleTarget = ALL_SALE_TARGET
  }
  return shopRequest<ShopModelResponse<AdminProductParamResponse>>({
    method: 'post',
    path: '/shop/product/v1/admin/bff/goods',
    data,
  })
}

export const fetchUpdateProductApi = ({
  data,
  goodsId,
}: {
  data: AdminProductParamResponse
  goodsId: string
}) => {
  if (data.sale.saleTarget?.targets.length === 0) {
    data.sale.saleTarget = ALL_SALE_TARGET
  }
  return shopRequest<ShopModelResponse<AdminProductParamResponse>>({
    method: 'put',
    path: `/shop/product/v1/admin/bff/goods/${goodsId}`,
    data,
  })
}

export const updateDisplayInfoProductApi = ({
  displayInfos,
  goodsId,
}: {
  displayInfos: DisplayInfosType[]
  goodsId: string
}) =>
  shopRequest<ShopModelResponse<UpdateDisplayInfoProductResponse>>({
    method: 'put',
    path: `/shop/product/v1/admin/goods/${goodsId}/display-info`,
    data: {
      displayInfos,
    },
  })

export const createOrderProductApi = (data: ShopOrderRequest) =>
  shopRequest<ShopModelResponse<ShopOrderResponse>>({
    method: 'post',
    path: `/shop/orders/v1/user/bff/payment`,
    data,
  })

export const createChargeProductApi = (data: ShopChargeRequest) =>
  shopRequest<ShopModelResponse<ShopOrderResponse>>({
    method: 'post',
    path: '/shop/orders/v1/user/bff/payment/charge',
    data,
  })

export const paymentInformationApi = (paymentId: string) =>
  shopRequest<ShopModelResponse<PaymentInformationResponse>>({
    method: 'get',
    path: `/shop/orders/v1/user/bff/payment/${paymentId}`,
  })

export const fetchListOrgCanSellProductApi = (
  data: FetchListOrgCanSellProductRequest,
) =>
  request<ListOrgCanSellProductResponse>({
    method: 'get',
    path: `/admin/api/v1/org/goods/${data.goodsId}/associated`,
    params: data.payload,
  })

export const getListOrgRegisteredProductApi = (goodsId: string) =>
  shopRequest<
    ShopModelResponse<{
      orgIds: string[]
    }>
  >({
    method: 'get',
    path: `/shop/product/v1/admin/goods/${goodsId}/sale-target`,
  })

export const registerProductSaleTargetApi = (data: {
  goodsId: string
  orgIds: number[]
}) =>
  shopRequest({
    method: 'put',
    path: `/shop/product/v1/admin/goods/${data.goodsId}/sale-target`,
    data: {orgIds: data.orgIds},
  })
export const unregisterProductSaleTargetApi = (data: {
  goodsId: string
  orgIds: number[]
}) =>
  shopRequest({
    method: 'delete',
    path: `/shop/product/v1/admin/goods/${data.goodsId}/sale-target/by-org`,
    params: {id: data.orgIds.join(',')},
  })

export const fetchListProductHistoryOrganizationApi = (
  query: ProductPurchaseHistoryOrganizationPayload,
) =>
  shopRequest<ShopModelResponse<ProductInquiryType>>({
    method: 'get',
    path: '/shop/orders/v1/admin/bff/payment',
    params: query,
  })

export const cancelPaymentApi = ({
  paymentId,
  reason,
}: {
  paymentId: string
  reason: string
}) =>
  shopRequest<ShopModelResponse<null>>({
    method: 'post',
    path: `/shop/orders/v1/admin/payment/${paymentId}/cancel`,
    data: {
      reason,
    },
  })

export const getPaymentDetailApi = (paymentId: string) =>
  shopRequest<ShopModelResponse<PaymentInformationResponse>>({
    method: 'get',
    path: `/shop/orders/v1/user/bff/payment/${paymentId}`,
  })

export const registerProductAssignLicenseApi = (data: {
  goodsId: string
  orgIds: number[]
  licenseStartDate?: string
  licenseEndDate?: string
}) =>
  shopRequest({
    method: 'post',
    path: `/shop/product/v1/admin/goods/${data.goodsId}/assign-license`,
    data: {
      orgIds: data.orgIds,
      ...(data.licenseStartDate && {licenseStartDate: data.licenseStartDate}),
      ...(data.licenseEndDate && {licenseEndDate: data.licenseEndDate}),
    },
  })

export const getLicenseDetailByOrg = (query: URLSearchParams) =>
  shopRequest<ShopModelResponse<ListLicenses[]>>({
    method: 'get',
    path: `/shop/licenses/v1/admin/license/by-org?${query.toString()}`,
  })

export const changeLicenseStatus = (payload: {
  data: {
    status: LicenseStatus
    reasonCode: string
    reason: string
    changedByName: string
  }
  query: {
    licenseId: string
  }
}) =>
  shopRequest({
    method: 'put',
    path: `/shop/licenses/v1/admin/license/${payload.query.licenseId}/status`,
    data: payload.data,
  })

export const getAvailableLicenseItems = () =>
  shopRequest<ShopModelResponse<AvailableLicenses[]>>({
    method: 'get',
    path: `/shop/licenses/v1/user/license/usable-items`,
  })

export const getLicenseDetailByOrgLogs = (
  licenseId: string,
  from?: string,
  to?: string,
) =>
  shopRequest<ShopModelResponse<ListLicensesLogs>>({
    method: 'get',
    path: `/shop/licenses/v1/admin/license/${licenseId}/logs`,
    params: !from || !to ? undefined : {from, to},
  })

export const registerManualLicense = (payload: {
  licenseId: string
  data: RegisterManualLicensePayload
}) =>
  shopRequest({
    method: 'put',
    path: `/shop/licenses/v1/admin/license/${payload.licenseId}/item/offer`,
    data: payload.data,
  })

export const getInvoiceTargetList = (query: GetInvoiceTargetListRequest) => {
  return shopRequest<ShopModelResponse<GetInvoiceTargetListResponse>>({
    method: 'get',
    path: `/shop/licenses/v1/admin/invoice/invoice-targets`,
    params: query,
  })
}

export const getInvoiceByOrgAndMonth = (
  query: GetInvoiceByOrgAndMonthRequest,
) => {
  return shopRequest<ShopModelResponse<InvoiceInformation>>({
    method: 'get',
    path: `/shop/licenses/v1/admin/invoice/by-org-and-month`,
    params: query,
  })
}

export const getInvoiceFileUploadUrl = (
  query: GetInvoiceFileUploadUrlRequest,
) => {
  return shopRequest<ShopModelResponse<InvoiceFileUploadUrl>>({
    method: 'post',
    path: '/shop/licenses/v1/admin/invoice/report-file/upload-url',
    data: query,
  })
}

export const createInvoiceApi = (payload: CreateInvoicePayload) => {
  return shopRequest<ShopModelResponse<InvoiceInformation>>({
    method: 'post',
    path: `/shop/licenses/v1/admin/invoice`,
    data: payload,
  })
}

export const updateInvoiceApi = ({
  invoiceId,
  ...payload
}: UpdateInvoicePayload) => {
  return shopRequest<ShopModelResponse<InvoiceInformation>>({
    method: 'put',
    path: `/shop/licenses/v1/admin/invoice/${invoiceId}`,
    data: payload,
  })
}

export const getLicenseByOrgAndMonth = (
  query: GetInvoiceByOrgAndMonthRequest,
) => {
  return shopRequest<ShopModelResponse<LicenseByMonth[]>>({
    method: 'get',
    path: '/shop/licenses/v1/admin/invoice/licenses',
    params: query,
  })
}

export const uploadInvoiceFileToS3 = async (request: ApiRequest) =>
  uploadS3Request(request)

export const uploadGoodAttachmentFileToS3 = async (request: ApiRequest) =>
  uploadGoodAttachmentRequest(request)

export const getLicenseExpiredDate = (goodsId: string) => {
  return shopRequest<ShopModelResponse<{estimatedExpiredDate: string}>>({
    method: 'get',
    path: `/shop/licenses/v1/user/license/estimate-expired-date/${goodsId}`,
  })
}

export const switchProductPositionApi = (payload: {
  previousGoodsId: string | null
  nextGoodsId: string | null
  goodsId: string
}) => {
  return shopRequest<ShopModelResponse<null>>({
    method: 'put',
    path: `/shop/product/v1/admin/goods/${payload.goodsId}/order`,
    data: {
      previousGoodsId: payload.previousGoodsId,
      nextGoodsId: payload.nextGoodsId,
    },
  })
}

export const changeProductArchiveStatusApi = (payload: {
  goodsId: string
  isArchived: boolean
}) => {
  return shopRequest<ShopModelResponse<null>>({
    method: 'put',
    path: `/shop/product/v1/admin/goods/${payload.goodsId}/archive`,
    data: {
      isArchived: payload.isArchived,
    },
  })
}

export const uploadGoodAttachmentApi = (payload: {
  fileName: string
  contentType: string
}) => {
  return shopRequest<ShopModelResponse<FileUploadUrlResponse>>({
    method: 'post',
    path: `/shop/product/v1/admin/goods/attachments/upload-url`,
    data: payload,
  })
}

export const getSalesSlipApi = (paymentId: string) =>
  shopRequest<ShopModelResponse<ISalesSlip>>({
    method: 'get',
    path: `/shop/orders/v1/admin/payment/${paymentId}/sales-slip`,
  })

export const updateSalesSlipApi = (payload: IUpdateSalesSlipRequest) =>
  shopRequest<ShopModelResponse<ISalesSlip | null>>({
    method: 'put',
    path: `/shop/orders/v1/admin/payment/${payload.paymentId}/sales-slip`,
    data: payload,
  })

export const getLicenseSearchByOrg = (payload: SearchOrgByLicenseRequest) =>
  shopRequest<ShopModelResponse<OrgByLicenseInquiryType>>({
    method: 'get',
    path: '/shop/licenses/v1/admin/license/search-by-org',
    params: payload,
  })

export const getRelatedPaymentApi = (paymentId: string) =>
  shopRequest<ShopModelResponse<Payment[]>>({
    method: 'get',
    path: `/shop/orders/v1/admin/payment/${paymentId}/related-payment`,
  })
